import { useEffect } from "react";
import { useStores } from "stores/setup/use-store";
import { runInAction } from "mobx";

/**
 * T027: Network Status Check Hook
 *
 * Monitors online/offline status and updates radar error state accordingly.
 *
 * Features:
 * - Listens to browser online/offline events
 * - Sets error message when connection is lost
 * - Clears error and retries metadata fetch when connection is restored
 * - Works seamlessly with T025 error notifications
 */
export const useRadarNetworkStatus = () => {
  const {
    dataStores: { radarMapStore },
  } = useStores();

  useEffect(() => {
    const handleOnline = () => {
      console.log("Network: Online - Retrying radar metadata fetch");

      // Clear connection error
      runInAction(() => {
        if (radarMapStore.errorMessage === "radarLegend.error.connectionLost") {
          radarMapStore.errorMessage = null;
        }
      });

      // Retry fetching metadata if radar is shown
      if (radarMapStore.showRadar) {
        radarMapStore.fetchMetadata();
      }
    };

    const handleOffline = () => {
      console.log("Network: Offline - Setting connection error");

      // Set connection lost error
      runInAction(() => {
        radarMapStore.errorMessage = "radarLegend.error.connectionLost";
      });
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initial status
    if (!navigator.onLine && radarMapStore.showRadar) {
      handleOffline();
    }

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [radarMapStore]);
};
